import { parseCsv } from './csv'
import { REQUIRED_UPLOAD_COLUMNS } from './constants'

// One row per setting per warehouse, the same shape the sheet has. The screen shows these rows and
// nothing else, so what is on screen and what gets sent are the same thing.

// The one status word the screens need without asking: a row with none is still to be answered.
// Every other status word, its colour and its wording come from the endpoint's vocabulary.
export const UNRESOLVED = 'unresolved'

// The review endpoint groups its questions and attaches the warehouses each one covers. The sheet
// does not: it carries a row per warehouse. Flattening here keeps one shape for both ways in.
export function fromApiQuestions(apiQuestions) {
    const rows = []
    for (const question of apiQuestions || []) {
        const options = Array.isArray(question.options) ? question.options : []
        const valuesSeen = (question.valuesSeen || []).map(seen => seen.value).join('; ')
        for (const applies of question.appliesTo || []) {
            rows.push({
                id: `${applies.warehouseId}-${applies.varName}`,
                status: UNRESOLVED,
                location_details: applies.location || '',
                question: question.question || '',
                values_seen: valuesSeen,
                reason: question.reason || '',
                // Composed by the endpoint, so the sheet, this screen and anything else read the
                // same wording.
                notes: question.notes || '',
                required: !!question.required,
                proposed_type: '',
                proposed_value: '',
                warehouse_id: applies.warehouseId,
                var_name: applies.varName,
                options,
            })
        }
    }
    return rows
}

// What the read wrote without asking, in the same shape, so a warehouse is one list and an
// assumed value is corrected the same way a question is answered.
export function fromWritten(written) {
    return (written || []).map(row => ({
        ...row,
        id: `${row.warehouse_id}-${row.var_name}`,
        options: [],
    }))
}

// A sheet read back in. Its columns are already the row, so this only adds what the screen needs.
function fromSheet(sheetRows) {
    return (sheetRows || []).map(row => ({
        ...row,
        id: `${row.warehouse_id}-${row.var_name}`,
        options: [],
    }))
}

// A run leaves two sheets, the questions and the assumed values, and either can come back
// answered. Both merge into one review: a row is a warehouse and a var, so the same row appearing
// in both sheets is one row, the later sheet winning. Takes text rather than files, so the reading
// stays with the screen and the merging stays testable.
export function mergeSheetRows(sheets) {
    const rowsById = new Map()
    for (const sheet of sheets) {
        const { columns, rows } = parseCsv(sheet.text)
        const missingColumns = REQUIRED_UPLOAD_COLUMNS.filter(name => !columns.includes(name))
        if (missingColumns.length) {
            throw new Error(`${sheet.name} is missing: ${missingColumns.join(', ')}. `
                + 'Use a sheet downloaded from the run.')
        }
        fromSheet(rows).forEach(row => rowsById.set(row.id, row))
    }
    if (!rowsById.size) throw new Error('Those sheets have no rows in them.')
    return [...rowsById.values()]
}

// Every row carrying a value. A row left empty is not sent, so it stays unconfigured.
export function toDecisions(rows) {
    return rows.filter(row => row.proposed_value !== '' && row.proposed_value != null)
}

export function countWarehouses(rows) {
    return new Set(rows.map(row => row.warehouse_id)).size
}

// The rows of one warehouse belong together: a person answers a facility's questions at once, not
// the same question across facilities. First appearance sets the order, so it follows the sheet.
export function byWarehouse(rows) {
    const groups = new Map()
    rows.forEach(row => {
        const group = groups.get(row.warehouse_id)
        if (group) {
            group.rows.push(row)
            return
        }
        groups.set(row.warehouse_id, {
            warehouseId: row.warehouse_id,
            location: row.location_details || row.warehouse_id,
            rows: [row],
        })
    })
    return [...groups.values()]
}
